// SELF — your active deities as ONE scene in the temple hall (template screen
// 3): the five faculties for this Step stand free in the hall — no frames —
// each lit from behind by a gold radiance, the one the Step needs dominant.
// Seats already secured stand small and faint on the terraces behind them
// (part of the structure); seats not yet reached are silhouettes higher up,
// in cloud. Tap a figure to talk to it. The Step bar returns to NOW.
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Image, PanResponder, Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Defs, RadialGradient, Stop, Ellipse } from 'react-native-svg';
import { TIER_LABEL, TIER_ORDER, type GearId } from '../olmps/ascension';
import type { SelfState, SeatView } from './useSelf';
import type { NowStep } from './useCurrentStep';
import { GEAR_LABEL, cutoutFor } from './roster';
import { maladyCutoutFor } from './maladyCutouts';
import { POINTS_UNIT } from '../olmps/gameSettings';
import { EdgeTabs } from './EdgeTabs';
import { GlassCard, GlassFill, Kicker } from './ui';
import { DISPLAY_FONT, SECTOR_COLOR, SECTOR_NAME, makeStyles, useV10 } from './theme';

// Pentagon of the five in play (cx = centre x, base = feet line, h = figure
// height), all as fractions of the stage. Back row first so the front row
// overlaps it, like a group standing in depth.
const SLOTS: { gear: GearId; cx: number; base: number; h: number }[] = [
  { gear: 'orient', cx: 0.5, base: 0.52, h: 0.4 },
  { gear: 'revise', cx: 0.18, base: 0.7, h: 0.38 },
  { gear: 'regulate', cx: 0.82, base: 0.7, h: 0.38 },
  { gear: 'direct', cx: 0.72, base: 0.86, h: 0.46 },
  { gear: 'execute', cx: 0.29, base: 0.86, h: 0.46 },
];
const FIG_ASPECT = 0.72; // cutout width / height (3:4 portraits trimmed)

export function SelfView({
  self,
  step,
  onOpenDeity,
  onBack,
}: {
  self: SelfState;
  step: NowStep | null;
  onOpenDeity: (seatId: string) => void;
  /** Pan back up to the orb. */
  onBack: () => void;
}) {
  const t = useV10();
  const styles = useStyles();
  const { width } = useWindowDimensions();
  const W = Math.min(width, 520);
  const H = W * 1.5;
  const current = step?.gear ?? null;
  const [page, setPage] = useState<number | null>(null);
  type Mode = 'maladies' | 'current' | 'deities';
  const MODES: { id: Mode; label: string }[] = [
    { id: 'maladies', label: 'Maladies' },
    { id: 'current', label: 'Current' },
    { id: 'deities', label: 'Deities' },
  ];
  const [mode, setMode] = useState<Mode>('current');
  const modeIdx = MODES.findIndex((m) => m.id === mode);
  const trackW = Math.min(W - 48, 300);
  const segW = trackW / 3;
  const thumb = useRef(new Animated.Value(modeIdx)).current;
  useEffect(() => {
    Animated.timing(thumb, { toValue: modeIdx, duration: 260, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
  }, [modeIdx, thumb]);
  // Per-slot crossfade: 1 = deity layer visible, 0 = malady layer visible.
  const dv = useRef<Record<string, Animated.Value>>({});
  const dvOf = (g: string) => (dv.current[g] ??= new Animated.Value(1));
  const workingIdx = Math.max(0, TIER_ORDER.indexOf(self.workingTier));
  const tierIdx = page ?? workingIdx;
  const tier = TIER_ORDER[tierIdx];
  const byGear = Object.fromEntries(self.all.filter((v) => v.seat.tier === tier).map((v) => [v.seat.gear, v])) as Record<GearId, SeatView>;
  const wantDeity = (g: GearId) => (mode === 'deities' ? 1 : mode === 'maladies' ? 0 : byGear[g]?.holder === 'deity' ? 1 : 0);
  const sig = SLOTS.map((sl) => wantDeity(sl.gear)).join('');
  useEffect(() => {
    SLOTS.forEach((sl) => Animated.timing(dvOf(sl.gear), { toValue: wantDeity(sl.gear), duration: 320, easing: Easing.inOut(Easing.cubic), useNativeDriver: true }).start());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);
  // The other tiers, relative to the one shown: lower = secured terraces, higher = veiled in cloud.
  const secured = self.all.filter((v) => TIER_ORDER.indexOf(v.seat.tier) < tierIdx);
  const veiled = self.all.filter((v) => TIER_ORDER.indexOf(v.seat.tier) > tierIdx);
  // Paging slides: the shown five exit sideways, the next five enter from the
  // opposite side. The swipe tracks the finger, then snaps or springs back.
  const sx = useRef(new Animated.Value(0)).current;
  const busy = useRef(false);
  const live = useRef({ tierIdx, W });
  live.current = { tierIdx, W };
  const slideTo = (dir: -1 | 1) => {
    const { tierIdx: cur, W: w } = live.current;
    const to = cur + dir;
    if (busy.current || to < 0 || to >= TIER_ORDER.length) {
      Animated.spring(sx, { toValue: 0, useNativeDriver: true }).start();
      return;
    }
    busy.current = true;
    // dir +1 (▶ next): current exits left, next enters from the right.
    Animated.timing(sx, { toValue: -dir * w, duration: 170, easing: Easing.in(Easing.cubic), useNativeDriver: true }).start(() => {
      setPage(to);
      sx.setValue(dir * w);
      Animated.timing(sx, { toValue: 0, duration: 190, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start(() => {
        busy.current = false;
      });
    });
  };
  const slideRef = useRef(slideTo);
  slideRef.current = slideTo;
  const swipe = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_e, g) => !busy.current && Math.abs(g.dx) > 18 && Math.abs(g.dx) > Math.abs(g.dy) * 1.5,
      onPanResponderMove: (_e, g) => {
        const { tierIdx: cur } = live.current;
        const to = cur + (g.dx < 0 ? 1 : -1);
        sx.setValue(to < 0 || to >= TIER_ORDER.length ? g.dx * 0.25 : g.dx);
      },
      onPanResponderRelease: (_e, g) => {
        if (g.dx < -60) slideRef.current(1);
        else if (g.dx > 60) slideRef.current(-1);
        else Animated.spring(sx, { toValue: 0, useNativeDriver: true }).start();
      },
      onPanResponderTerminate: () => Animated.spring(sx, { toValue: 0, useNativeDriver: true }).start(),
    }),
  ).current;
  return (
    <View style={{ flex: 1 }}>
    <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
      <View style={styles.head}>
        <View style={[styles.seg, { width: trackW }]}>
          <GlassFill radius={999} />
          <Animated.View style={[styles.thumb, { width: segW - 4, transform: [{ translateX: Animated.add(Animated.multiply(thumb, segW), 2) }] }]}>
            <LinearGradient colors={[t.goldBright, t.gold]} style={[StyleSheet.absoluteFill, { borderRadius: 999 }]} pointerEvents="none" />
          </Animated.View>
          {MODES.map((m) => (
            <Pressable key={m.id} onPress={() => setMode(m.id)} style={[styles.segBtn, { width: segW }]} hitSlop={4}>
              <Text style={[styles.segText, mode === m.id && { color: t.onGold }]}>{m.label}</Text>
            </Pressable>
          ))}
        </View>
      </View>

      <View style={[styles.stage, { width: W, height: H }]} {...swipe.panHandlers}>
        <Animated.View style={[StyleSheet.absoluteFill, { transform: [{ translateX: sx }] }]} pointerEvents="box-none">
        {/* Higher terraces, in cloud: seats not yet reached, as silhouettes. */}
        {veiled.length ? (
          <View style={[styles.cloudBand, { top: H * 0.02, height: H * 0.2 }]} pointerEvents="none">
            <LinearGradient colors={['rgba(255,255,255,0)', t.mode === 'dark' ? 'rgba(243,234,215,0.10)' : 'rgba(255,255,255,0.45)', 'rgba(255,255,255,0)']} style={StyleSheet.absoluteFill} />
            {veiled.slice(0, 10).map((v, i, arr) => {
              const n = arr.length;
              const x = (0.08 + (0.84 * (i + 0.5)) / n) * W;
              const h = H * 0.11;
              const src = cutoutFor(v.seat.id);
              return src ? (
                <Image
                  key={v.seat.id}
                  source={src}
                  style={{ position: 'absolute', left: x - (h * FIG_ASPECT) / 2, bottom: 0, width: h * FIG_ASPECT, height: h, tintColor: t.mode === 'dark' ? 'rgba(243,234,215,0.11)' : 'rgba(255,255,255,0.5)' }}
                  resizeMode="contain"
                />
              ) : null;
            })}
          </View>
        ) : null}

        {/* Terraces behind: already-secured seats, small, part of the structure. */}
        {secured.length ? (
          <View style={[styles.terrace, { top: H * 0.2, height: H * 0.22 }]} pointerEvents="none">
            {secured.slice(0, 10).map((v, i, arr) => {
              const n = arr.length;
              const x = (0.1 + (0.8 * (i + 0.5)) / n) * W;
              const h = H * 0.2;
              const src = cutoutFor(v.seat.id);
              return src ? (
                <Image key={v.seat.id} source={src} style={{ position: 'absolute', left: x - (h * FIG_ASPECT) / 2, bottom: 0, width: h * FIG_ASPECT, height: h, opacity: 0.45 }} resizeMode="contain" />
              ) : null;
            })}
            <LinearGradient colors={t.mode === 'dark' ? ['rgba(7,11,23,0)', 'rgba(7,11,23,0.75)'] : ['rgba(225,240,255,0)', 'rgba(225,240,255,0.55)']} style={[StyleSheet.absoluteFill, { top: '55%' }]} />
          </View>
        ) : null}

        {/* The five in play. */}
        {SLOTS.map((slot) => {
          const v = byGear[slot.gear];
          if (!v) return null;
          const lit = slot.gear === current;
          const h = slot.h * H * (lit ? 1.08 : 1);
          const w = h * FIG_ASPECT;
          return (
            <Figure
              key={slot.gear}
              v={v}
              dv={dvOf(slot.gear)}
              lit={lit}
              w={w}
              h={h}
              style={{ position: 'absolute', left: slot.cx * W - w / 2, top: slot.base * H - h }}
              onPress={() => onOpenDeity(v.seat.id)}
            />
          );
        })}
        </Animated.View>
        {/* Floor mist so feet sit in the hall. */}
        <LinearGradient pointerEvents="none" colors={t.mode === 'dark' ? ['rgba(7,11,23,0)', 'rgba(7,11,23,0.85)'] : ['rgba(225,240,255,0)', 'rgba(225,240,255,0.8)']} style={[StyleSheet.absoluteFill, { top: H * 0.78 }]} />
        <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFill, { zIndex: 6, transform: [{ translateX: sx }] }]}>
        {/* Labels: back row above the heads, front row at the feet — never over another figure. */}
        {SLOTS.map((slot) => {
          const v = byGear[slot.gear];
          if (!v) return null;
          const lit = slot.gear === current;
          const front = slot.base > 0.8;
          const labelW = W * (front ? 0.42 : 0.31);
          const figH = slot.h * H * (lit ? 1.08 : 1);
          const top = front ? slot.base * H + 2 : slot.base * H - figH - H * 0.095;
          const showMal = mode === 'maladies' || (mode === 'current' && v.holder === 'malady');
          const mal = v.maladyName || 'Malady';
          const st = v.standing;
          const name = mode === 'maladies' ? mal : showMal ? `${mal} holds` : v.seat.name;
          const sub =
            mode === 'maladies' ? `opposes ${v.seat.name}` : mode === 'deities' ? GEAR_LABEL[v.seat.gear].tag : `${st.position == null ? '–' : (st.position > 0 ? '+' : '') + st.position} · ${st.label}`;
          return (
            <View key={`l-${slot.gear}`} style={[styles.figText, { left: Math.max(0, Math.min(W - labelW, slot.cx * W - labelW / 2)), width: labelW, top }]}>
              <Text style={[styles.figGear, lit && { color: t.goldBright, fontSize: 18 }]}>{GEAR_LABEL[v.seat.gear].name.toUpperCase()}</Text>
              <Text style={[styles.figName, showMal && { color: t.maladyText }]} numberOfLines={2}>
                {name}
              </Text>
              {mode !== 'current' ? <Text style={styles.figTag} numberOfLines={1}>{sub}</Text> : null}
            </View>
          );
        })}
        </Animated.View>
      </View>

      {step ? (
        <Pressable onPress={onBack} style={{ alignSelf: 'stretch', marginTop: 4 }}>
          <GlassCard style={styles.stepBar}>
            <View style={[styles.stepIcon, { borderColor: step.sector ? SECTOR_COLOR[step.sector] : t.gold }]}>
              <Text style={[styles.stepIconText, { color: step.sector ? SECTOR_COLOR[step.sector] : t.gold }]}>
                {step.sector ? SECTOR_NAME[step.sector].slice(0, 1) : '●'}
              </Text>
            </View>
            <View style={{ flex: 1, gap: 2 }}>
              <Kicker>Back to the orb</Kicker>
              <Text style={styles.stepTitle} numberOfLines={1}>
                {step.title}
              </Text>
              <Text style={styles.stepWhy} numberOfLines={1}>
                {step.why}
              </Text>
            </View>
            <View style={styles.chev}>
              <Text style={styles.chevText}>▲</Text>
            </View>
          </GlassCard>
        </Pressable>
      ) : null}

      <Text style={styles.foot}>
        {self.rank} · {self.securedCount}/15 seats secured · tap a deity to talk
      </Text>
    </ScrollView>
    <EdgeTabs
      up={onBack}
      left={tierIdx > 0 ? () => slideTo(-1) : undefined}
      right={tierIdx < TIER_ORDER.length - 1 ? () => slideTo(1) : undefined}
      labels={{ up: 'Back up to the orb', left: 'Previous five', right: 'Next five' }}
    />
    </View>
  );
}

/** The opposing malady, a transparent cutout sized like the deity cutouts. */
function MaladyFigure({ id, tool, w, h }: { id: string; tool: string; w: number; h: number }) {
  const src = maladyCutoutFor(id, tool);
  return src ? <Image source={src} style={{ width: w, height: h }} resizeMode="contain" /> : null;
}

function Figure({ v, dv, lit, w, h, style, onPress }: { v: SeatView; dv: Animated.Value; lit: boolean; w: number; h: number; style: object; onPress: () => void }) {
  const t = useV10();
  const styles = useStyles();
  const src = cutoutFor(v.seat.id);
  const glowW = w * (lit ? 1.9 : 1.4);
  return (
    <Pressable onPress={onPress} style={[style, { width: w, height: h, zIndex: lit ? 3 : 1 }]}>
      {/* Radiance behind the figure (brighter, wider for the lit one). */}
      <Svg pointerEvents="none" width={glowW} height={glowW * 1.1} style={{ position: 'absolute', left: w / 2 - glowW / 2, top: h * 0.3 - (glowW * 1.1) / 2 }}>
        <Defs>
          <RadialGradient id={`g-${v.seat.id}`} cx="50%" cy="50%" r="50%">
            <Stop offset="0" stopColor="#f0cf85" stopOpacity={lit ? 0.75 : 0.35} />
            <Stop offset="0.55" stopColor="#d6b46a" stopOpacity={lit ? 0.28 : 0.12} />
            <Stop offset="1" stopColor="#d6b46a" stopOpacity={0} />
          </RadialGradient>
        </Defs>
        <Ellipse cx={glowW / 2} cy={(glowW * 1.1) / 2} rx={glowW / 2} ry={(glowW * 1.1) / 2} fill={`url(#g-${v.seat.id})`} />
      </Svg>
      <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFill, { opacity: dv }]}>
        {src ? <Image source={src} style={{ width: w, height: h }} resizeMode="contain" /> : null}
      </Animated.View>
      <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFill, { opacity: Animated.subtract(1, dv) }]}>
        <MaladyFigure id={v.maladyId} tool={v.seat.id} w={w} h={h} />
      </Animated.View>
    </Pressable>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    scroll: { paddingHorizontal: 0, paddingTop: 58, paddingBottom: 36, gap: 10, alignItems: 'center' },
    seg: { height: 34, borderRadius: 999, flexDirection: 'row', alignItems: 'center', overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, marginTop: 6 },
    thumb: { position: 'absolute', left: 0, top: 2, bottom: 2, borderRadius: 999, overflow: 'hidden', boxShadow: `0 0 12px ${t.goldGlow}` } as object,
    segBtn: { height: 34, alignItems: 'center', justifyContent: 'center' },
    segText: { color: t.ivorySoft, fontSize: 12, fontWeight: '800', letterSpacing: 0.5 },
    up: { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderWidth: 1, borderColor: t.goldBright, boxShadow: `0 0 14px ${t.goldGlow}` } as object,
    upText: { color: t.onGold, fontSize: 14, fontWeight: '800' },
    head: { alignItems: 'center', gap: 4, marginBottom: 2 },
    title: { fontFamily: DISPLAY_FONT, color: t.ivory, fontSize: 20, letterSpacing: 3, textTransform: 'uppercase' },
    stage: { alignSelf: 'center', overflow: 'visible' },
    cloudBand: { position: 'absolute', left: 0, right: 0 },
    terrace: { position: 'absolute', left: 0, right: 0 },
    figText: { position: 'absolute', alignItems: 'center', gap: 1 },
    figGear: { fontFamily: DISPLAY_FONT, color: t.ivory, fontSize: 16, letterSpacing: 1.8, textShadowColor: t.mode === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.85)', textShadowRadius: 8 },
    figTag: { color: t.ivory, fontSize: 11, fontWeight: '600', textShadowColor: t.mode === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.85)', textShadowRadius: 10, textShadowOffset: { width: 0, height: 1 } },
    figName: { color: t.goldBright, fontSize: 10, fontWeight: '800', letterSpacing: 0.6, marginTop: 2, textAlign: 'center', textShadowColor: t.mode === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.85)', textShadowRadius: 10, textShadowOffset: { width: 0, height: 1 } },
    stepBar: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, marginHorizontal: 12 },
    stepIcon: { width: 44, height: 44, borderRadius: 22, borderWidth: 1.5, alignItems: 'center', justifyContent: 'center', backgroundColor: t.mode === 'dark' ? 'rgba(7,11,23,0.5)' : 'rgba(255,255,255,0.7)' },
    stepIconText: { fontSize: 16, fontWeight: '900' },
    stepTitle: { color: t.ivory, fontSize: 17, fontWeight: '800' },
    stepWhy: { color: t.ivorySoft, fontSize: 12.5 },
    chev: { width: 34, height: 34, borderRadius: 17, borderWidth: 1, borderColor: t.gold, alignItems: 'center', justifyContent: 'center' },
    chevText: { color: t.goldBright, fontSize: 20, fontWeight: '700', marginTop: -2 },
    foot: { color: t.ivoryFaint, fontSize: 11.5, textAlign: 'center' },
  }),
);
